import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import type {
  ChangePasswordRequest,
  LoginRequest,
  User,
} from '@/api/types';

export const ME_QUERY_KEY = ['me'] as const;

/**
 * Single source of truth for "who am I?" Any component that needs the
 * current user should use this hook — never fetch `authApi.me()` directly.
 * This guarantees the query cache stays consistent after login/logout.
 *
 * Also exposes a bare `logout()` callback so components that only need
 * to sign the user out (e.g. the command palette) don't have to mount
 * their own mutation.
 */
export function useAuth() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const query = useQuery<User | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.me,
    retry: false,
    staleTime: 60_000,
  });

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.clear();
      navigate('/login', { replace: true });
    }
  }, [qc, navigate]);

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isLoggedIn: !!query.data,
    mustChangePassword: !!query.data?.must_change_password,
    refetch: query.refetch,
    error: query.error,
    logout,
  };
}

/** POST /api/auth/login — on success seeds the `me` query. */
export function useLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation<User, Error, LoginRequest>({
    mutationFn: (req) => authApi.login(req),
    onSuccess: (user) => {
      qc.setQueryData(ME_QUERY_KEY, user);
      if (user.must_change_password) {
        navigate('/first-login', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    },
  });
}

/** POST /api/auth/logout — wipes the query cache + routes to /login. */
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation<void, Error, void>({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.clear();
      navigate('/login', { replace: true });
    },
  });
}

/** POST /api/auth/change-password — handles the first-login flow. */
export function useChangePassword() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation<{ ok: boolean }, Error, ChangePasswordRequest>({
    mutationFn: (req) => authApi.changePassword(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate('/', { replace: true });
    },
  });
}
